import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../CoreFunctions';
import { exportToExcel } from '../utils/exportUtils';

const IncomeStatement = () => {
  // Data
  const [ccTxns, setCcTxns] = useState([]); // revolvers (credit cards) for both income tagging and expenses charges
  const [cashTxns, setCashTxns] = useState([]); // cash account transactions
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Controls
  const [rangeMode, setRangeMode] = useState('ytd'); // last30 | month | last3 | ytd | last12 | custom
  const [month, setMonth] = useState(() => {
    const d = new Date(); const mm = String(d.getMonth()+1).padStart(2,'0'); return `${d.getFullYear()}-${mm}`;
  });
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(); d.setMonth(0, 1); return d.toISOString().slice(0, 10);
  });
  const [customEnd, setCustomEnd] = useState(() => {
    return new Date().toISOString().slice(0, 10);
  });
  const [groupBy, setGroupBy] = useState('monthly'); // weekly | monthly | quarterly | annually
  const [cumulative, setCumulative] = useState(false);
  const [netOnly, setNetOnly] = useState(false); // if true, show Net Income series instead of separate income/expenses
  const [lagEnabled, setLagEnabled] = useState(true); // if true, end date is the end of M-2 (month before the prior month)
  const [activeTab, setActiveTab] = useState('averages'); // 'totals' | 'averages' | 'transactions'
  const [deselectedSeriesIds, setDeselectedSeriesIds] = useState(new Set());
  const [otherIncomeDeselected, setOtherIncomeDeselected] = useState(false);

  // Chart refs
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  // Date range
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    let refEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    
    if (lagEnabled) {
      // "1 month lag" means we exclude the current month and the month before it (M and M-1)
      // because transactions for M-1 might not be complete yet.
      // So in April, we want to end at Feb 28.
      refEnd = new Date(now.getFullYear(), now.getMonth() - 1, 0, 23, 59, 59, 999);
    }
    
    const end = refEnd;
    let start;
    if (rangeMode === 'last30') {
      start = new Date(end); start.setDate(start.getDate() - 29); start.setHours(0,0,0,0);
    } else if (rangeMode === 'month') {
      const [y, m] = month.split('-').map(Number);
      start = new Date(y, m - 1, 1, 0,0,0,0);
      const last = new Date(y, m, 0); last.setHours(23,59,59,999);
      return { startDate: start, endDate: last };
    } else if (rangeMode === 'last3') {
      start = new Date(end); start.setMonth(start.getMonth() - 2); start.setDate(1); start.setHours(0,0,0,0);
    } else if (rangeMode === 'ytd') {
      start = new Date(end.getFullYear(), 0, 1, 0,0,0,0);
    } else if (rangeMode === 'last12') {
      start = new Date(end); start.setMonth(start.getMonth() - 11); start.setDate(1); start.setHours(0,0,0,0);
    } else if (rangeMode === 'custom') {
      const [sy, sm, sd] = customStart.split('-').map(Number);
      start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
      const [ey, em, ed] = customEnd.split('-').map(Number);
      const e = new Date(ey, em - 1, ed, 23, 59, 59, 999);
      return { startDate: start, endDate: e };
    } else {
      start = new Date(end); start.setDate(start.getDate() - 29); start.setHours(0,0,0,0);
    }
    return { startDate: start, endDate: end };
  }, [rangeMode, month, customStart, customEnd, lagEnabled]);

  const startStr = startDate?.toISOString().slice(0, 10);
  const endStr = endDate?.toISOString().slice(0, 10);

  // Extract unique recurring income series
  const recurringSeries = useMemo(() => {
    const seriesMap = new Map();
    const processTxn = (t) => {
      if (t.linked_income_stream_id) {
        const sid = String(t.linked_income_stream_id);
        if (!seriesMap.has(sid)) {
          seriesMap.set(sid, t.linked_income_info?.display_name || `Series ${sid}`);
        }
      }
      if (Array.isArray(t.children)) {
        t.children.forEach(processTxn);
      }
    };
    cashTxns.forEach(processTxn);
    ccTxns.forEach(processTxn);
    
    return Array.from(seriesMap.entries()).map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cashTxns, ccTxns]);

  const toggleSeries = (id) => {
    setDeselectedSeriesIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isIncomeCategoryLocal = (cat, t = null) => {
    const c = (cat || '').toLowerCase();
    const matches = ['income_recurring', 'income_nonrecurring', 'tax_refund', 'income', 'interest_income', 'dividend_income', 'debt_disbursement'].includes(c);
    const hasStream = !!(t && t.linked_income_stream_id);
    console.log('DEBUG: isIncomeCategoryLocal', cat, 'hasStream?', hasStream, 'matches?', matches, 'RESULT?', matches || hasStream);
    return matches || hasStream;
  };

  // Fetch data from same APIs as Income and Expenses pages
  useEffect(() => {
    const load = async () => {
      setLoading(true); setError('');
      try {
        const [rvRes, caRes] = await Promise.all([
          axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: startStr, end_date: endStr }  }),
          axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: startStr, end_date: endStr }  }),
        ]);
        // Handle wrapped response { transactions: [], available_expense_categories: [] }
        const rvTxns = Array.isArray(rvRes.data) ? rvRes.data : (rvRes.data?.transactions || []);
        const caTxns = Array.isArray(caRes.data) ? caRes.data : (caRes.data?.transactions || []);

        // For revolvers, keep all; we'll filter by category later per rules
        setCcTxns(rvTxns);
        setCashTxns(caTxns);
      } catch (e) {
        console.error('Income Statement load error', e);
        setError('Failed to load data.');
      } finally {
        setLoading(false);
      }
    };
    if (startStr && endStr) load();
    else { setCcTxns([]); setCashTxns([]); setLoading(false); }
  }, [startStr, endStr]);

  // Helpers to classify
  const isIncomeCategory = (cat, t = null) => {
    const c = (cat || '').toLowerCase();
    // common income categories
    if (['income_recurring', 'income_nonrecurring', 'tax_refund', 'income', 'interest_income', 'dividend_income', 'debt_disbursement'].includes(c)) return true;
    // Check if it's linked to an income stream even if category is something else
    if (t && t.linked_income_stream_id) return true;
    // Special case for credit card payments that are actually income (e.g. statement credit from income source)
    // usually handled by linked_income_stream_id but being safe
    return false;
  };

  // Buckets for aggregation
  const buckets = useMemo(() => {
    if (!startDate || !endDate) return [];
    const list = [];

    const keyFor = (date) => {
      const y = date.getUTCFullYear();
      if (groupBy === 'weekly') {
        const tmp = new Date(date);
        const day = (tmp.getUTCDay() + 6) % 7; 
        tmp.setUTCDate(tmp.getUTCDate() - day + 3);
        const th = new Date(tmp);
        const oneJan = new Date(Date.UTC(th.getUTCFullYear(),0,1));
        const week = Math.floor(((th - oneJan) / 86400000 + oneJan.getUTCDay() + 1) / 7) + 1;
        return `${th.getUTCFullYear()}-W${String(week).padStart(2,'0')}`;
      } else if (groupBy === 'monthly') {
        return `${y}-${String(date.getUTCMonth()+1).padStart(2,'0')}`;
      } else if (groupBy === 'quarterly') {
        const q = Math.floor(date.getUTCMonth()/3) + 1; return `${y}-Q${q}`;
      }
      return `${y}`; // annually
    };

    const addStep = (date) => {
      if (groupBy === 'weekly') date.setUTCDate(date.getUTCDate()+7);
      else if (groupBy === 'monthly') date.setUTCMonth(date.getUTCMonth()+1);
      else if (groupBy === 'quarterly') date.setUTCMonth(date.getUTCMonth()+3);
      else date.setUTCFullYear(date.getUTCFullYear()+1);
    };

    const startD = new Date(startDate);
    // Use UTC for bucket boundaries to match transaction processing
    const utcStart = new Date(Date.UTC(startD.getFullYear(), startD.getMonth(), startD.getDate()));
    
    if (groupBy === 'weekly') {
      const day = (utcStart.getUTCDay() + 6) % 7; utcStart.setUTCDate(utcStart.getUTCDate() - day);
    } else if (groupBy === 'monthly') {
      utcStart.setUTCDate(1);
    } else if (groupBy === 'quarterly') {
      const qm = Math.floor(utcStart.getUTCMonth()/3)*3; utcStart.setUTCMonth(qm, 1);
    } else if (groupBy === 'annually') {
      utcStart.setUTCMonth(0, 1);
    }

    const dEnd = new Date(endDate);
    const utcEnd = new Date(Date.UTC(dEnd.getFullYear(), dEnd.getMonth(), dEnd.getDate()));

    for (let cur = new Date(utcStart); cur <= utcEnd; addStep(cur)) {
      list.push({ key: keyFor(cur), start: new Date(cur) });
    }
    return list;
  }, [startDate, endDate, groupBy]);

  // Consolidate data processing into a single useMemo to improve performance with large datasets
  const processedData = useMemo(() => {
    // To feed the transactions table
    const filteredIncomeTxns = [];
    const filteredExpenseTxns = [];

    const toNum = (val) => {
      try { return typeof val === 'object' && val && val.amount != null ? Number(val.amount) : Number(val); } catch(e) { return 0; }
    };

    // 2. Aggregate into chart data
    const labels = buckets.map(b => b.key);
    const incomeMap = new Map(labels.map(k => [k, 0]));
    const expenseMap = new Map(labels.map(k => [k, { total: 0, loans: 0, cc: 0, cash: 0, mortgages: 0, auto: 0, helocs: 0 }]));

    const keyFor = (dateStr) => {
      if (!dateStr) return null;
      // Using UTC to avoid local timezone shifts for YYYY-MM-DD strings
      const [y, m, d] = dateStr.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      const year = dt.getUTCFullYear();
      let k;
      if (groupBy === 'weekly') {
        const tmp = new Date(dt);
        const day = (tmp.getUTCDay() + 6) % 7; 
        tmp.setUTCDate(tmp.getUTCDate() - day + 3);
        const th = new Date(tmp);
        const oneJan = new Date(Date.UTC(th.getUTCFullYear(),0,1));
        const week = Math.floor(((th - oneJan) / 86400000 + oneJan.getUTCDay() + 1) / 7) + 1;
        k = `${th.getUTCFullYear()}-W${String(week).padStart(2,'0')}`;
      } else if (groupBy === 'monthly') {
        k = `${year}-${String(dt.getUTCMonth()+1).padStart(2,'0')}`;
      } else if (groupBy === 'quarterly') {
        const q = Math.floor(dt.getUTCMonth()/3) + 1; k = `${year}-Q${q}`;
      } else {
        k = `${year}`;
      }
      console.log('DEBUG: keyFor', dateStr, '->', k, 'incomeMap has?', incomeMap.has(k));
      return k;
    };

    const pushChild = (ch) => {
      const k = (ch.expense_category || '').toLowerCase();
      const isInc = isIncomeCategory(k, ch);
      if (!isInc) return;
      if (ch.linked_income_stream_id) {
        if (deselectedSeriesIds.has(String(ch.linked_income_stream_id))) return;
      } else {
        if (otherIncomeDeselected) return;
      }
      const amt = Math.abs(Number(ch.amount) || 0);
      if (amt !== 0) {
        const key = keyFor(ch.transaction_date);
        if (key && incomeMap.has(key)) {
          filteredIncomeTxns.push(ch);
        }
      }
    };

    console.log('DEBUG: processedData START', { cashCount: cashTxns?.length, ccCount: ccTxns?.length, bucketCount: buckets?.length, labels });
    
    // helper to find parent for split validation
    const findCashParent = (pid) => (cashTxns || []).find(p => p.id === pid);
    const findCcParent = (pid) => (ccTxns || []).find(p => p.id === pid);

    const isOtherIncome = (t) => !t.linked_income_stream_id;

    (cashTxns || []).forEach(t => {
      const hasKids = (t.children_count && t.children_count > 0) || (Array.isArray(t.children) && t.children.length > 0);
      if (hasKids) {
        (Array.isArray(t.children) ? t.children : []).forEach(pushChild);
      } else if (!t.parent) {
        const cat = (t.expense_category || '').toLowerCase();
        if (isIncomeCategory(cat, t)) {
          if (t.linked_income_stream_id) {
            if (deselectedSeriesIds.has(String(t.linked_income_stream_id))) return;
          } else {
            if (otherIncomeDeselected) return;
          }
          const amt = toNum(t.amount);
          if (amt !== 0) {
            const key = keyFor(t.transaction_date);
            if (key && incomeMap.has(key)) {
              filteredIncomeTxns.push(t);
            }
          }
        }
      }
    });

    (ccTxns || []).forEach(t => {
      const hasKids = (t.children_count && t.children_count > 0) || (Array.isArray(t.children) && t.children.length > 0);
      if (hasKids) {
        (Array.isArray(t.children) ? t.children : []).forEach(pushChild);
      } else if (!t.parent) {
        const cat = (t.expense_category || '').toLowerCase();
        if (isIncomeCategory(cat, t)) {
          if (t.linked_income_stream_id) {
            if (deselectedSeriesIds.has(String(t.linked_income_stream_id))) return;
          } else {
            if (otherIncomeDeselected) return;
          }
          const amt = toNum(t.amount);
          if (amt !== 0) {
            const key = keyFor(t.transaction_date);
            if (key && incomeMap.has(key)) {
              filteredIncomeTxns.push(t);
            }
          }
        }
      }
    });

    // 2. Aggregate into chart data
    filteredIncomeTxns.forEach(t => { 
      const k = keyFor(t.transaction_date); 
      if (k && incomeMap.has(k)) {
        const amt = isIncomeCategory((t.expense_category || '').toLowerCase(), t) ? toNum(t.amount) : 0;
        incomeMap.set(k, (incomeMap.get(k) || 0) + amt); 
      }
    });
    
    // Process cash account expenses
    (cashTxns || []).forEach(t => {
      const cat = (t.expense_category || 'none').toLowerCase();
      const linkedType = t.linked_debt_info?.type || null;
      const isLoan = (cat === 'payments') && (linkedType === 'auto_loan' || linkedType === 'mortgage' || linkedType === 'heloc');

      // Avoid double-counting split transactions: only count children OR non-split parents
      const hasChildren = !!(t.children_count || (t.has_children === true) || (t.children && t.children.length > 0));
      if (t.parent != null || hasChildren) {
          if (hasChildren && !isLoan) {
            // Check if this parent transaction should be in the filtered list (e.g. for display purposes)
            // but we don't aggregate its amount if it has children that are also processed.
            // Actually, the current logic for filteredExpenseTxns.push(t) is inside the amt < 0 check.
          }
          if (t.parent && !hasChildren) {
              const parent = findCashParent(t.parent);
              if (parent) {
                  const pCat = (parent.expense_category || 'none').toLowerCase();
                  const pType = parent.linked_debt_info?.type;
                  const pIsLoan = (pCat === 'payments') && (pType === 'auto_loan' || pType === 'mortgage' || pType === 'heloc');
                  if (pIsLoan) return;
              }
          }
      }

      if (!isIncomeCategory(cat, t)) {
        let amt = toNum(t.amount);
        const includePayment = (cat === 'payments') ? isLoan : true;
        if (includePayment && amt !== 0) {
          const k = keyFor(t.transaction_date);
          if (k && incomeMap.has(k)) {
            filteredExpenseTxns.push(t);
            const bucket = expenseMap.get(k);
            if (amt < 0) {
                const val = -amt;
                bucket.total += val;
                if (isLoan) {
                  bucket.loans += val;
                  if (linkedType === 'mortgage') bucket.mortgages += val;
                  else if (linkedType === 'auto_loan') bucket.auto += val;
                  else if (linkedType === 'heloc') bucket.helocs += val;
                }
                else bucket.cash += val;
            } else {
                // Positive expense amount = refund/credit
                const val = amt;
                bucket.total -= val;
                if (isLoan) {
                    bucket.loans -= val;
                    if (linkedType === 'mortgage') bucket.mortgages -= val;
                    else if (linkedType === 'auto_loan') bucket.auto -= val;
                    else if (linkedType === 'heloc') bucket.helocs -= val;
                } else {
                    bucket.cash -= val;
                }
            }
          }
        }
      }
    });

    // Process credit card expenses
    (ccTxns || []).forEach(t => {
      const cat = (t.expense_category || 'none').toLowerCase();
      const hasChildren = !!(t.children_count || (t.has_children === true) || (t.children && t.children.length > 0));
      
      // Avoid double-counting split transactions
      if (t.parent != null || hasChildren) {
          if (hasChildren) return;
          if (t.parent) {
            const parent = findCcParent(t.parent);
            if (parent && (parent.expense_category || '').toLowerCase() === 'payments' && parent.revolver_type === 'heloc') return;
          }
      }

      if (!isIncomeCategory(cat, t)) {
        let amt = toNum(t.amount);
        const k = keyFor(t.transaction_date);
        if (k && incomeMap.has(k)) {
          if (cat === 'payments') {
            // Note: HELOC payments are already counted via Cash Account transactions
          } else if (!['payments','rewards_cashback'].includes(cat) && amt !== 0) {
            filteredExpenseTxns.push(t);
            const bucket = expenseMap.get(k);
            if (amt > 0) {
                // CC expense is positive
                const val = amt;
                bucket.total += val;
                bucket.cc += val;
            } else {
                // CC credit/refund is negative
                const val = -amt;
                bucket.total -= val;
                bucket.cc -= val;
            }
          }
        }
      }
    });

    let incomeArr = labels.map(k => incomeMap.get(k) || 0);
    let expenseArr = labels.map(k => (expenseMap.get(k)?.total || 0)).map(v => -v);

    const makeCum = (arr) => { let s = 0; return arr.map(v => { s += v; return s; }); };

    let datasets;
    if (netOnly) {
      let net = labels.map((_, i) => (incomeArr[i] + expenseArr[i]));
      if (cumulative) net = makeCum(net);
      datasets = [{ label: 'Net Income', data: net, backgroundColor: '#60a5fa' }];
    } else {
      if (cumulative) {
        incomeArr = makeCum(incomeArr);
        expenseArr = makeCum(expenseArr);
      }
      datasets = [
        { label: 'Income', data: incomeArr, backgroundColor: '#16a34a' },
        { label: 'Expenses', data: expenseArr, backgroundColor: '#ef4444' },
      ];
    }

    // 3. Totals - Should only include what is in the buckets to stay consistent with the chart
    let totalIncome = 0, totalExpenses = 0;
    
    // We sum up the buckets instead of the raw rows to ensure we only count 
    // transactions that fall within the finalized bucket ranges (accounting for any start/end normalization)
    labels.forEach(k => {
      totalIncome += (incomeMap.get(k) || 0);
      totalExpenses += (expenseMap.get(k)?.total || 0);
    });

    console.log('DEBUG: processedData END', { filteredIncomeCount: filteredIncomeTxns.length, filteredExpenseCount: filteredExpenseTxns.length });
    return {
      chartData: { labels, datasets },
      totals: { income: totalIncome, expenses: totalExpenses, net: totalIncome - totalExpenses },
      expenseMap,
      incomeMap,
      filteredIncomeTxns,
      filteredExpenseTxns
    };
  }, [cashTxns, ccTxns, buckets, groupBy, cumulative, netOnly, deselectedSeriesIds, otherIncomeDeselected]);

  const { chartData, totals, expenseMap, incomeMap, filteredIncomeTxns, filteredExpenseTxns } = processedData;

  const ExcelIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
  );

  const handleExport = () => {
    const incomeRows = filteredIncomeTxns.map(t => ({
      ...t,
      type: 'Income',
      displayAmount: Math.abs(Number(t.amount || 0))
    }));
    const expenseRows = filteredExpenseTxns.map(t => ({
      ...t,
      type: 'Expense',
      displayAmount: -Math.abs(Number(t.amount || 0))
    }));

    const allRows = [...incomeRows, ...expenseRows].sort((a, b) => b.transaction_date.localeCompare(a.transaction_date));

    const columns = [
      { header: 'Date', key: 'transaction_date' },
      { header: 'Type', key: 'type' },
      { header: 'Description', key: 'description' },
      { header: 'Amount', key: 'displayAmount' },
      { header: 'Category', key: 'expense_category' },
    ];

    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_');
    const filename = `IncomeStatement_Transactions_${dateStr}`;
    exportToExcel(allRows, filename, columns);
  };

  // 4. Calculate stats for the footer metrics
  const stats = useMemo(() => {
    if (!expenseMap || expenseMap.size === 0) return null;
    
    // We only want to average the buckets that are actually represented in the chart
    const buckets = Array.from(expenseMap.values());
    if (buckets.length === 0) return null;

    const keys = ['total', 'loans', 'mortgages', 'auto', 'helocs', 'cc', 'cash'];
    const results = {};

    keys.forEach(key => {
      const vals = buckets.map(b => {
        if (key === 'nonLoans') return (b.cc || 0) + (b.cash || 0);
        return b[key] || 0;
      });
      const n = vals.length;
      const sum = vals.reduce((a, b) => a + b, 0);
      const avg = sum / n;
      const sqDiffs = vals.map(v => Math.pow(v - avg, 2));
      const stdDev = Math.sqrt(sqDiffs.reduce((a, b) => a + b, 0) / n);
      
      results[key === 'nonLoans' ? 'nonLoans' : key] = { avg, stdDev };
    });
    
    // Special case for nonLoans since it's not a direct key in bucket but we want it in keys loop if we handle it there
    // Re-doing the loop slightly cleaner to include nonLoans
    const allKeys = ['total', 'loans', 'mortgages', 'auto', 'helocs', 'nonLoans', 'cc', 'cash'];
    const finalResults = {};
    allKeys.forEach(key => {
        const vals = buckets.map(b => {
            if (key === 'nonLoans') return (b.cc || 0) + (b.cash || 0);
            return b[key] || 0;
        });
        const n = vals.length;
        const sum = vals.reduce((a, b) => a + b, 0);
        const avg = sum / n;
        const sqDiffs = vals.map(v => Math.pow(v - avg, 2));
        const stdDev = Math.sqrt(sqDiffs.reduce((a, b) => a + b, 0) / n);
        finalResults[key] = { avg, stdDev };
    });

    // Add Income stats
    const incomeVals = Array.from(incomeMap.values());
    if (incomeVals.length > 0) {
      const n = incomeVals.length;
      const sum = incomeVals.reduce((a, b) => a + b, 0);
      const avg = sum / n;
      const sqDiffs = incomeVals.map(v => Math.pow(v - avg, 2));
      const stdDev = Math.sqrt(sqDiffs.reduce((a, b) => a + b, 0) / n);
      finalResults['income'] = { avg, stdDev };
    }

    return finalResults;
  }, [expenseMap, incomeMap]);

  // Chart render
  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    if (!chartData.labels || chartData.labels.length === 0) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels: chartData.labels, datasets: chartData.datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
          y: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
        },
        plugins: {
          legend: { position: 'bottom', labels: { color: '#e5e7eb' } },
          tooltip: {
            backgroundColor: '#1f2937', titleColor: '#e5e7eb', bodyColor: '#e5e7eb',
            bodyFont: { family: 'monospace' },
            callbacks: {
              label: (ctx) => {
                const label = ctx.dataset.label || '';
                const val = ctx.parsed.y;
                let res = `${label}: ${formatCurrency(val)}`;
                
                if (label === 'Expenses') {
                  const key = ctx.label;
                  const bucket = expenseMap.get(key);
                  if (bucket && bucket.total > 0) {
                    let totalVal, loansVal, mortgagesVal, autoVal, helocsVal, ccVal, cashVal, nonLoanVal;

                    if (cumulative) {
                      // Calculate cumulative totals for all sub-categories up to the hovered point
                      totalVal = 0; loansVal = 0; mortgagesVal = 0; autoVal = 0; helocsVal = 0; ccVal = 0; cashVal = 0;
                      const idx = chartData.labels.indexOf(key);
                      for (let i = 0; i <= idx; i++) {
                        const b = expenseMap.get(chartData.labels[i]);
                        if (b) {
                          totalVal += b.total;
                          loansVal += b.loans;
                          mortgagesVal += b.mortgages;
                          autoVal += b.auto;
                          helocsVal += b.helocs;
                          ccVal += b.cc;
                          cashVal += b.cash;
                        }
                      }
                      nonLoanVal = ccVal + cashVal;
                    } else {
                      totalVal = bucket.total;
                      loansVal = bucket.loans;
                      mortgagesVal = bucket.mortgages;
                      autoVal = bucket.auto;
                      helocsVal = bucket.helocs;
                      ccVal = bucket.cc;
                      cashVal = bucket.cash;
                      nonLoanVal = ccVal + cashVal;
                    }

                    const formatLine = (label, val, total) => {
                      const perc = total > 0 ? ((val / total) * 100).toFixed(1).padStart(5, ' ') : '  0.0';
                      const valStr = formatCurrency(val).padStart(12, ' ');
                      return `${label.padEnd(20, ' ')}${valStr} (${perc}%)`;
                    };

                    return [
                      formatLine(label, totalVal, totalVal),
                      formatLine('  • Loans', loansVal, totalVal),
                      formatLine('      • Mortgages', mortgagesVal, totalVal),
                      formatLine('      • Auto', autoVal, totalVal),
                      formatLine('      • HELOCs', helocsVal, totalVal),
                      formatLine('  • Non-Loans', nonLoanVal, totalVal),
                      formatLine('      • Credit Card', ccVal, totalVal),
                      formatLine('      • Cash Account', cashVal, totalVal)
                    ];
                  }
                }
                return res;
              }
            }
          }
        }
      }
    });

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [chartData, expenseMap, cumulative]);

  return (
    <div className="content-container">
      <h1>Income Statement Overview</h1>

      {/* Controls */}
      <div className="chart-controls" style={{ marginBottom: 12, display:'flex', flexWrap:'wrap', gap:12, alignItems:'flex-end' }}>
        <div className="control-group">
          <span className="control-label">Range:</span>
          <select value={rangeMode} onChange={e => setRangeMode(e.target.value)} className="control-select">
            <option value="last30">Past 30 days</option>
            <option value="month">Month</option>
            <option value="last3">Past 3 months</option>
            <option value="ytd">YTD</option>
            <option value="last12">Past 12 months</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        {rangeMode === 'month' && (
          <div className="control-group">
            <span className="control-label">Month:</span>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="control-select" />
          </div>
        )}
        {rangeMode === 'custom' && (
          <>
            <div className="control-group">
              <span className="control-label">From:</span>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="control-select" />
            </div>
            <div className="control-group">
              <span className="control-label">To:</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="control-select" />
            </div>
          </>
        )}
        <div className="control-group">
          <span className="control-label">Group by:</span>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="control-select">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
        <div className="control-group">
          <label style={{ display:'flex', alignItems:'center', gap:8 }}>
            <input type="checkbox" checked={cumulative} onChange={e => setCumulative(e.target.checked)} />
            <span className="control-label">Cumulative</span>
          </label>
        </div>
        <div className="control-group">
          <label style={{ display:'flex', alignItems:'center', gap:8 }}>
            <input type="checkbox" checked={netOnly} onChange={e => setNetOnly(e.target.checked)} />
            <span className="control-label">Net (Income - Expenses)</span>
          </label>
        </div>
        <div className="control-group">
          <label style={{ display:'flex', alignItems:'center', gap:8 }}>
            <input type="checkbox" checked={lagEnabled} onChange={e => setLagEnabled(e.target.checked)} />
            <span className="control-label">1 month lag</span>
          </label>
        </div>
      </div>

      {/* Recurring Income Series Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', background: '#1f2937', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Income Series:</span>
            
            {/* "Other Income" Toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
              <input 
                type="checkbox" 
                checked={!otherIncomeDeselected} 
                onChange={() => setOtherIncomeDeselected(!otherIncomeDeselected)} 
              />
              <span style={{ color: otherIncomeDeselected ? '#6b7280' : '#e5e7eb', fontStyle: 'italic' }}>Other Income</span>
            </label>

            {recurringSeries.map(s => (
              <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
                <input 
                  type="checkbox" 
                  checked={!deselectedSeriesIds.has(s.id)} 
                  onChange={() => toggleSeries(s.id)} 
                />
                <span style={{ color: deselectedSeriesIds.has(s.id) ? '#6b7280' : '#e5e7eb' }}>{s.name}</span>
              </label>
            ))}
            <div style={{ marginLeft: 'auto', fontWeight: 700, display: 'flex', gap: 16 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: '#9ca3af' }}>Inc:</span>
                <span style={{ color:'#16a34a' }}>{formatCurrency(Math.round(totals.income), 0)}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: '#9ca3af' }}>Exp:</span>
                <span style={{ color:'#ef4444' }}>{formatCurrency(Math.round(totals.expenses), 0)}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: '#9ca3af' }}>Net:</span>
                <span style={{ color: totals.net >= 0 ? '#16a34a' : '#ef4444' }}>{formatCurrency(Math.round(totals.net), 0)}</span>
              </div>
            </div>
          </div>
        </div>
        
        <button 
          onClick={handleExport}
          className="btn btn-outline-info"
          style={{ display: 'flex', alignItems: 'center', gap: 6, height: 'fit-content', whiteSpace: 'nowrap', padding: '8px 16px' }}
          title="Export Transactions to Excel"
        >
          <ExcelIcon /> Export to Excel
        </button>
      </div>

      {/* Chart */}
      <div style={{ background:'#0b1220', padding:10, borderRadius:8, border:'1px solid #374151', marginBottom:16 }}>
        {loading && <div>Loading chart…</div>}
        {error && <div className="alert alert-warning">{error}</div>}
        {!loading && !error && (
          <div className="chart-card" style={{ height: '380px', position: 'relative' }}>
            {(!chartData.labels || chartData.labels.length === 0) ? (
              <div className="text-center py-4">No data in selected range.</div>
            ) : (
              <canvas ref={canvasRef}></canvas>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      {!loading && !error && (
        <div style={{ display: 'flex', gap: 24, marginBottom: 16, borderBottom: '1px solid #374151', paddingLeft: 8 }}>
          <div 
            onClick={() => setActiveTab('totals')}
            style={{ 
              padding: '8px 16px', 
              cursor: 'pointer', 
              color: activeTab === 'totals' ? '#60a5fa' : '#9ca3af',
              borderBottom: activeTab === 'totals' ? '2px solid #60a5fa' : 'none',
              fontWeight: activeTab === 'totals' ? 700 : 400
            }}
          >
            Totals
          </div>
          <div 
            onClick={() => setActiveTab('averages')}
            style={{ 
              padding: '8px 16px', 
              cursor: 'pointer', 
              color: activeTab === 'averages' ? '#60a5fa' : '#9ca3af',
              borderBottom: activeTab === 'averages' ? '2px solid #60a5fa' : 'none',
              fontWeight: activeTab === 'averages' ? 700 : 400
            }}
          >
            Averages
          </div>
          <div 
            onClick={() => setActiveTab('transactions')}
            style={{ 
              padding: '8px 16px', 
              cursor: 'pointer', 
              color: activeTab === 'transactions' ? '#60a5fa' : '#9ca3af',
              borderBottom: activeTab === 'transactions' ? '2px solid #60a5fa' : 'none',
              fontWeight: activeTab === 'transactions' ? 700 : 400
            }}
          >
            Transactions
          </div>
        </div>
      )}

      {/* Data Table (Totals) */}
      {!loading && !error && activeTab === 'totals' && chartData.labels && chartData.labels.length > 0 && (
        <div style={{ background:'#0b1220', padding:10, borderRadius:8, border:'1px solid #374151', marginBottom:16, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', color: '#e5e7eb', fontFamily: 'monospace', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #374151' }}>
                <th style={{ textAlign: 'left', padding: 8 }}>Period</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Income</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Expenses</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Net</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Cum. Income</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Cum. Expenses</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Cum. Net</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = chartData.labels.map((label, idx) => {
                  let income, expenses, net;
                  
                  // If chart is NOT cumulative, the datasets already have the period-specific values
                  if (!cumulative) {
                    income = chartData.datasets.find(d => d.label === 'Income')?.data[idx] || 0;
                    expenses = chartData.datasets.find(d => d.label === 'Expenses')?.data[idx] || 0;
                    net = chartData.datasets.find(d => d.label === 'Net Income')?.data[idx] || (income + expenses);
                  } else {
                    // If chart IS cumulative, we need to extract period-specific values
                    // or better, calculate them from our source maps to be safe
                    income = incomeMap.get(label) || 0;
                    expenses = -(expenseMap.get(label)?.total || 0);
                    net = income + expenses;
                  }

                  return { label, income, expenses, net };
                });

                // Calculate cumulative values based on chronological order (original rows order)
                let cumInc = 0;
                let cumExp = 0;
                let cumNet = 0;
                const rowsWithCum = rows.map(r => {
                  cumInc += r.income;
                  cumExp += r.expenses;
                  cumNet += r.net;
                  return { ...r, cumInc, cumExp, cumNet };
                });

                // Reverse for display: latest at the top
                return rowsWithCum.reverse().map((r) => (
                  <tr key={r.label} style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ textAlign: 'left', padding: 8 }}>{r.label}</td>
                    <td style={{ textAlign: 'right', padding: 8, color: '#16a34a' }}>{formatCurrency(r.income, 0)}</td>
                    <td style={{ textAlign: 'right', padding: 8, color: '#ef4444' }}>{formatCurrency(r.expenses, 0)}</td>
                    <td style={{ textAlign: 'right', padding: 8 }}>{formatCurrency(r.net, 0)}</td>
                    <td style={{ textAlign: 'right', padding: 8, color: '#16a34a', opacity: 0.8 }}>{formatCurrency(r.cumInc, 0)}</td>
                    <td style={{ textAlign: 'right', padding: 8, color: '#ef4444', opacity: 0.8 }}>{formatCurrency(r.cumExp, 0)}</td>
                    <td style={{ textAlign: 'right', padding: 8, opacity: 0.8 }}>{formatCurrency(r.cumNet, 0)}</td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* Transactions Table */}
      {!loading && !error && activeTab === 'transactions' && (
        <div style={{ background:'#0b1220', padding:10, borderRadius:8, border:'1px solid #374151', marginBottom:16 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', color: '#e5e7eb', fontFamily: 'monospace', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #374151' }}>
                  <th style={{ textAlign: 'left', padding: 8 }}>Date</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Type</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Description</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Category</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const incomeRows = (filteredIncomeTxns || []).map(t => ({ ...t, type: 'Income', displayAmount: Math.abs(Number(t.amount || 0)) }));
                  const expenseRows = (filteredExpenseTxns || []).map(t => ({ ...t, type: 'Expense', displayAmount: -Math.abs(Number(t.amount || 0)) }));
                  const allRows = [...incomeRows, ...expenseRows].sort((a, b) => b.transaction_date.localeCompare(a.transaction_date));
                  
                  if (allRows.length === 0) {
                    return <tr><td colSpan="5" style={{ textAlign: 'center', padding: 16 }}>No transactions in selected range. (DEBUG: I:{filteredIncomeTxns.length}, E:{filteredExpenseTxns.length})</td></tr>;
                  }

                  return allRows.map((t, idx) => (
                    <tr key={`${t.id}-${t.type}-${idx}`} style={{ borderBottom: '1px solid #1f2937' }}>
                      <td style={{ textAlign: 'left', padding: 8 }}>{t.transaction_date}</td>
                      <td style={{ textAlign: 'left', padding: 8, color: t.type === 'Income' ? '#16a34a' : '#ef4444' }}>{t.type}</td>
                      <td style={{ textAlign: 'left', padding: 8, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.description}
                      </td>
                      <td style={{ textAlign: 'right', padding: 8, color: t.type === 'Income' ? '#16a34a' : '#ef4444' }}>
                        {formatCurrency(t.displayAmount, 2)}
                      </td>
                      <td style={{ textAlign: 'left', padding: 8 }}>{t.expense_category}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Averages Section */}
      {!loading && !error && activeTab === 'averages' && stats && (
        <div style={{ background:'#1f2937', padding:'16px 24px', borderRadius:8, border:'1px solid #374151', color:'#e5e7eb', fontFamily:'monospace' }}>
          <div style={{ display:'grid', gridTemplateColumns:'200px 1fr 1fr 1fr', gap:'8px 24px', borderBottom:'1px solid #374151', paddingBottom:8, marginBottom:8, fontWeight:700 }}>
            <div>Category</div>
            <div style={{ textAlign:'right' }}>Average ($)</div>
            <div style={{ textAlign:'right' }}>Std Dev ($)</div>
            <div style={{ textAlign:'right' }}>Std Dev (%)</div>
          </div>
          
          {[
            { label: 'Total Expenses', key: 'total', indent: 0, color: '#ef4444' },
            { label: 'Loans', key: 'loans', indent: 1 },
            { label: 'Mortgages', key: 'mortgages', indent: 2 },
            { label: 'Auto', key: 'auto', indent: 2 },
            { label: 'HELOCs', key: 'helocs', indent: 2 },
            { label: 'Non-Loans', key: 'nonLoans', indent: 1 },
            { label: 'Credit Card', key: 'cc', indent: 2 },
            { label: 'Cash Account', key: 'cash', indent: 2 },
            { label: 'Total Income', key: 'income', indent: 0, color: '#16a34a' },
          ].map(row => {
            const s = stats[row.key];
            if (!s) return null;
            
            let percOfTotal;
            if (row.key === 'income' || row.key === 'total') {
              percOfTotal = '100.0';
            } else {
              percOfTotal = stats.total.avg > 0 ? ((s.avg / stats.total.avg) * 100).toFixed(1) : '0.0';
            }
            
            const stdDevPerc = s.avg > 0 ? ((s.stdDev / s.avg) * 100).toFixed(1) : '0.0';
            
            return (
              <React.Fragment key={row.key}>
                <div style={{ display:'grid', gridTemplateColumns:'200px 1fr 1fr 1fr', gap:'8px 24px', padding:'2px 0' }}>
                  <div style={{ paddingLeft: row.indent * 20, color: row.color || 'inherit' }}>
                    {row.indent > 0 && '• '}{row.label}
                  </div>
                  <div style={{ textAlign:'right', whiteSpace:'pre', color: row.color || 'inherit' }}>
                    {formatCurrency(s.avg, 0).padStart(12, ' ')} ({percOfTotal.padStart(5, ' ')}%)
                  </div>
                  <div style={{ textAlign:'right', whiteSpace:'pre' }}>
                    {formatCurrency(s.stdDev, 0).padStart(12, ' ')}
                  </div>
                  <div style={{ textAlign:'right', whiteSpace:'pre' }}>
                    {stdDevPerc.padStart(5, ' ')}%
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Total Difference Row */}
          {stats.income && stats.total && (() => {
            const diff = stats.income.avg - stats.total.avg;
            const diffColor = diff >= 0 ? '#16a34a' : '#ef4444';
            return (
              <div style={{ 
                display:'grid', 
                gridTemplateColumns:'200px 1fr 1fr 1fr', 
                gap:'8px 24px', 
                padding:'8px 0', 
                marginTop: 8, 
                borderTop:'1px solid #374151',
                fontWeight: 700
              }}>
                <div>Total Difference</div>
                <div style={{ textAlign:'right', whiteSpace:'pre', color: diffColor }}>
                  {formatCurrency(diff, 0).padStart(12, ' ')}{' '.repeat(9)}
                </div>
                <div style={{ textAlign:'right' }}>-</div>
                <div style={{ textAlign:'right' }}>-</div>
              </div>
            );
          })()}
        </div>
      )}

      {/* End of content */}
    </div>
  );
};

export default IncomeStatement;
